/**
 * Firmware version normalisation (D29, ADR-0039), shared by the API, the web
 * form and (as a Python copy) the RAG ranking. Both copies are tested against
 * `firmware-normalisation.cases.json`: change the cases first.
 *
 * Steps: lowercase and trim; strip a leading `firmware` / `version` / `fw` /
 * `v` label (only when followed by a digit, a space or a separator, so
 * `vendor1.0` is kept); `_` and `-` become `.`; keep `[a-z0-9.]`; collapse
 * repeated dots and trim dots at both ends.
 *   "V1.2.3" -> "1.2.3", "fw 1_2_3" -> "1.2.3"
 */
const LEADING_LABEL = /^(?:firmware|version|fw|v)(?=$|[\s\d:_.-])/;
const LEADING_SEPARATORS = /^[\s:_.-]+/;

export function normaliseFirmwareVersion(raw: string): string {
  let value = raw.trim().toLowerCase();
  for (;;) {
    const stripped = value
      .replace(LEADING_SEPARATORS, '')
      .replace(LEADING_LABEL, '');
    if (stripped === value) break;
    value = stripped;
  }
  return value
    .replace(/[_-]/g, '.')
    .replace(/[^a-z0-9.]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+|\.+$/g, '');
}

export type FirmwareMatch = 'exact' | 'prefix' | 'mismatch';

/**
 * Compares a rule firmware with a charger firmware, both normalised.
 * Prefix only on a dot boundary: `1.2` matches `1.2.3`, not `1.20`.
 */
export function matchFirmwareVersion(
  ruleVersion: string,
  chargerVersion: string,
): FirmwareMatch {
  if (ruleVersion === chargerVersion) return 'exact';
  if (chargerVersion.startsWith(`${ruleVersion}.`)) return 'prefix';
  return 'mismatch';
}
